import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import InputLabel from '@material-ui/core/InputLabel';
import Radio from '@material-ui/core/Radio';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { rem } from 'polished';
import PropTypes from 'prop-types';
import React from 'react';

import { Button } from 'components/AORedesign';

const clampedText = {
  textAlign: 'center',
  overflow: 'hidden',
  display: '-webkit-box',
  '-webkit-line-clamp': 3,
  '-webkit-box-orient': 'vertical',
};

const iconCircle = (theme) => ({
  margin: `${rem(60)} auto 1rem`,
  width: rem(56),
  height: rem(56),
  borderRadius: rem(99),
  backgroundColor: theme.palette.secondary.main,
});

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'grid',
    gridGap: rem(16),
    margin: '0 auto',
    padding: 0,
    maxWidth: rem(1200),

    '@media (min-width: 42em)': {
      gridTemplateColumns: 'repeat(4, 0fr)',
    },
  },
  card: {
    width: rem(288),
    boxSizing: 'border-box',
    borderRadius: rem(4),
    position: 'relative',
    border: `${rem(1)} solid rgba(0,0,0,0.12)`,
  },
  selected: {
    borderColor: theme.palette.secondary.main,
  },
  recommendedMarker: {
    position: 'absolute',
    left: 0,
    top: 0,
    textTransform: 'uppercase',
    backgroundColor: theme.palette.jade[600],
    color: theme.palette.common.white,
    padding: `${rem(10.5)} ${rem(16)}`,
    fontSize: rem(12),
    fontFamily: `'Montserrat', sans-serif`,
    fontWeight: 600,
    borderRadius: `${rem(4)} 0 0 0`,
  },
  content: {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 0.2s ease-out',
  },
  radio: {
    fontSize: 'inherit',
    margin: 0,
    position: 'absolute',
    right: rem(12),
    top: rem(8),
    cursor: 'pointer',
    width: rem(20),
    height: rem(20),
    padding: 0,
  },
  iconContainer: {
    ...iconCircle(theme),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  icon: {
    width: rem(30),
    filter: 'brightness(0) invert(1)',
    height: 'auto',
  },
  noIcon: iconCircle(theme),
  title: {
    ...clampedText,
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: rem(20),
    lineHeight: rem(23.44),
    height: rem(75),
    padding: '0 1rem',
  },
  description: {
    ...clampedText,
    fontSize: rem(14),
    lineHeight: rem(20),
    height: rem(60),
    padding: '0 1rem',
  },
  learnMore: {
    marginTop: '0.5rem',
    padding: '0.5rem 1rem',
    width: '100%',
    textAlign: 'center',
  },
}));

const ProductCard = ({
  products,
  idKey,
  recommendedKey,
  nameKey,
  logoUrlKey,
  descriptionKey,
  onChangeCallback,
  error,
  onLearnMoreClick,
  selectedProduct,
}) => {
  const classes = useStyles();

  if (!products?.length) {
    return null;
  }

  const isRecommended = (product) => Boolean(product.StatusTags?.[recommendedKey]);

  const alphabeticalComparisonFunction = (a, b) => {
    if (a[nameKey] < b[nameKey]) {
      return -1;
    }
    if (a[nameKey] > b[nameKey]) {
      return 1;
    }
    return 0;
  };

  // Recommended products go first; both sets stay in alphabetical order.
  const recommendedProducts = products.filter(isRecommended).sort(alphabeticalComparisonFunction);
  const otherProducts = products.filter((product) => !isRecommended(product)).sort(alphabeticalComparisonFunction);

  return (
    <Box className={classes.grid}>
      {[...recommendedProducts, ...otherProducts].map((product) => {
        const productValue = JSON.stringify(product);
        const isSelected = selectedProduct === productValue;

        return (
          <div key={`${product[idKey]}`}>
            <Card>
              <InputLabel className={`${classes.card} ${isSelected ? classes.selected : ''}`}>
                {isRecommended(product) && <Box className={classes.recommendedMarker}>RECOMMENDED</Box>}
                <Radio className={classes.radio} checked={isSelected} onChange={(e) => onChangeCallback(e.target.value)} value={productValue} />
                <Box className={classes.content}>
                  {product[logoUrlKey]?.length ? (
                    <div className={classes.iconContainer}>
                      <img className={classes.icon} src={product[logoUrlKey]} alt="" />
                    </div>
                  ) : (
                    <div className={classes.noIcon} />
                  )}
                  {product[nameKey] && (
                    <Typography className={classes.title} variant="h6" id={product[nameKey].toLowerCase().replace(':', '').split(' ').join('-')}>
                      {product[nameKey]}
                    </Typography>
                  )}
                  {product[descriptionKey] && (
                    <Typography className={classes.description} variant="body1">
                      {product[descriptionKey]}
                    </Typography>
                  )}
                  {product[idKey] && (
                    <Button
                      id={`product-card-learn-more-${product[idKey]}`}
                      className={classes.learnMore}
                      color="secondary"
                      variant="text"
                      onClick={(e) => onLearnMoreClick(e, productValue)}>
                      Learn More
                    </Button>
                  )}
                </Box>
              </InputLabel>
            </Card>
            {error ? (
              <Typography component={'div'}>
                <Box fontSize={12} color="error.main" mt={0.5}>
                  Select a Product.
                </Box>
              </Typography>
            ) : null}
          </div>
        );
      })}
    </Box>
  );
};

ProductCard.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object),
  idKey: PropTypes.string,
  recommendedKey: PropTypes.string,
  nameKey: PropTypes.string,
  descriptionKey: PropTypes.string,
  logoUrlKey: PropTypes.string,
  onChangeCallback: PropTypes.func,
  error: PropTypes.bool,
  onLearnMoreClick: PropTypes.func,
  selectedProduct: PropTypes.string,
};

export default ProductCard;
